
import React from 'react';
import { Link } from 'react-router-dom';
import { Map, Compass, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const AccountMapping = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Enterprise Account Mapping Training | BPO Consultants"
                description="Master the art of navigating complex organizations. Learn to potential champions, uncover blockers, and strategic account planning."
                keywords="Account Mapping, Enterprise Sales Strategy, Strategic Account Management, Org Chart Navigation, Sales Territory Planning"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200" alt="Strategic Planning" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Map size={14} className="inline mr-1" /> Module 1: Strategy
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Enterprise <br /> Account Mapping
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Don't just sell to a contact. Sell to an organization.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">Navigating the Labyrinth</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            In enterprise sales, the decision isn't made by one person. It's a consensus game. This module teaches you how to map out the political landscape of formatting Global 2000 companies to identify who holds the real power.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "Org Chart Archaeology",
                                    desc: "Techniques for reconstructing hidden reporting lines using LinkedIn Sales Navigator and public data."
                                },
                                {
                                    title: "The White Space Analysis",
                                    desc: "Identifying cross-sell and up-sell opportunities within existing large accounts."
                                },
                                {
                                    title: "Buying Center Identification",
                                    desc: "Distinguishing between the User Buyer, Technical Buyer, and Economic Buyer."
                                },
                                {
                                    title: "Territory Planning",
                                    desc: "Prioritizing your patch to focus 80% of your effort on the 20% of accounts that will yield revenue."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <Compass size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    {/* SGE / FAQ Section */}
                    <FadeIn className="mb-16">
                        <h2 className="text-2xl font-bold text-[#20222D] mb-6">Why This Matters</h2>
                        <div className="mt-4 space-y-4">
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">What tools do we use?</h3>
                                <p className="text-gray-600">We train on LinkedIn Sales Navigator, ZoomInfo, and Lucidchart for visual mapping.</p>
                            </div>
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">How is this different from lead gen?</h3>
                                <p className="text-gray-600">Lead gen is finding a name. Account mapping is finding the *relationship* between names to influence a deal.</p>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Master the Map</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default AccountMapping;
